import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzCommentModule } from 'ng-zorro-antd/comment';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzFormModule } from 'ng-zorro-antd/form';
import { FormsModule } from '@angular/forms';
import { NzInputModule } from 'ng-zorro-antd/input';
import { CommonService } from '../../../../../../shared/services/common.service';
import { InsightsDataService } from '../../insights-data.service';
import { finalize, Observable, of, Subject, takeUntil } from 'rxjs';
import { DrawerService } from '../../../../../../core/services/drawer.service';
import { DRAWER_IDS } from '../../../../../../core/constants/drawer-ids.constant';
import { DataNote, FrequencyDrawerParams } from '../../insights-data.interface';
import { AuthService } from '../../../../../../core/services/auth.service';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { LoaderService } from '../../../../../../core/services/loader.service';
import { ConfirmDialogComponent } from '../../../../../../components/confirm-dialog/confirm-dialog.component';
import { ErrorResponse } from '../../../../../../core/models/error.models';
import { UserDatePipe } from '../../../../../../shared/pipes/user-date.pipe';
import { NoDataComponent } from '../../../../../../pages/content/no-data/no-data.component';

@Component({
  selector: 'app-manage-data-notes',
  standalone: true,
  imports: [
    CommonModule,
    NzGridModule,
    NzCommentModule,
    NzCardModule,
    NzAvatarModule,
    NzButtonModule,
    SvgIconComponent,
    NzFormModule,
    NzInputModule,
    FormsModule,
    NzSpinModule,
    UserDatePipe,
    ConfirmDialogComponent,
    NoDataComponent,
  ],
  templateUrl: './manage-data-notes.component.html',
  styleUrl: './manage-data-notes.component.scss',
})
export class ManageDataNotesComponent implements OnDestroy {
  note = '';
  updateNote = '';
  selectedNoteData: DataNote | null = null;
  selectedNoteIdForDeleting: number | null = null;
  private destroy$ = new Subject<void>();
  drawerParams: FrequencyDrawerParams = {} as FrequencyDrawerParams;
  loading$: Observable<boolean> = of(false);
  notes: DataNote[] = [];
  userId: number | null = null;
  showConfirmDialog = false;
  constructor(
    private commonSrv: CommonService,
    private insightsDataSrv: InsightsDataService,
    private drawerService: DrawerService,
    private loaderService: LoaderService,
    private authService: AuthService,
    private cd: ChangeDetectorRef
  ) {
    this.loading$ = this.loaderService.loading$;
    this.setupDrawerSubscription();
    this.authService.userData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.userId = user?.id || 0;
      });
  }

  generateAlias(name: string): string {
    const nameParts = name.split(' ');

    let alias = '';
    nameParts.forEach(part => {
      alias += part.charAt(0).toUpperCase();
    });

    return alias;
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<FrequencyDrawerParams>(DRAWER_IDS.DATA_NOTES)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.drawerParams = params;
          this.getNotesData();
        }
      });
  }

  getNotesData() {
    this.loaderService.show();
    this.insightsDataSrv
      .getComments(this.drawerParams.unitId, this.drawerParams.selectedDate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.notes = response.result;
          this.note = '';
        },
        error: error => {
          this.commonSrv.notify('error', error.message);
        },
        complete: () => {
          this.loaderService.hide();
        },
      });
  }

  selectNote(noteData: DataNote) {
    this.selectedNoteData = noteData;
    this.updateNote = this.selectedNoteData.comment;
  }

  addNote() {
    if (!this.note) {
      return;
    }
    this.loaderService.show();
    const payload = {
      activityDataUnitId: this.drawerParams.unitId,
      comment: this.note,
      dataDate: this.drawerParams.selectedDate,
    };
    this.insightsDataSrv
      .addComment(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.commonSrv.notify('success', 'Note added successfully');
          this.note = '';
          this.getNotesData();
        },
        error: error => {
          this.loaderService.hide();
          this.commonSrv.notify('error', error.message);
        },
        complete: () => {
          this.loaderService.hide();
        },
      });
  }

  editNote() {
    if (!this.updateNote || !this.selectedNoteData) {
      return;
    }
    this.loaderService.show();
    const payload = {
      id: this.selectedNoteData.id,
      comment: this.updateNote,
    };
    this.insightsDataSrv
      .editComment(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.commonSrv.notify('success', 'Note updated successfully');
          this.selectedNoteData = null;
          const record = this.notes.find(n => n.id === payload.id);
          if (record) {
            record.comment = payload.comment;
          }
          this.updateNote = '';
          this.cd.detectChanges();
        },
        error: error => {
          this.loaderService.hide();
          this.commonSrv.notify('error', error.message);
        },
        complete: () => {
          this.loaderService.hide();
        },
      });
  }

  handleDelete(): void {
    if (!this.selectedNoteIdForDeleting) {
      return;
    }
    this.loaderService.show();
    this.insightsDataSrv
      .deleteComment(this.selectedNoteIdForDeleting)
      .pipe(
        finalize(() => {
          this.loaderService.show();
          this.showConfirmDialog = false;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => {
          this.commonSrv.notify('success', 'Note deleted successfully');
          this.selectedNoteIdForDeleting = null;
          this.getNotesData();
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors?.map(
            err => err.detailedMessage || err.description
          );
          this.commonSrv.notify('error', errorMessages, message);
        },
        complete: () => {
          this.loaderService.hide();
        },
      });
  }

  handleCancelDelete(): void {
    this.showConfirmDialog = false;
    this.selectedNoteIdForDeleting = null;
  }

  deleteNote(Note: DataNote) {
    this.selectedNoteIdForDeleting = Note.id;
    this.showConfirmDialog = true;
  }

  cancelSelection() {
    this.selectedNoteData = null;
    this.note = '';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
